import PhoneActionBar from "features/onboarding/common/PhoneActionBar";
import { useSelectRealUser } from "@shared/utils/hooks/misc";
import {
  useOnboardingAnalytics,
  useOnboardingStepView,
} from "analytics/onboarding";
import useMedia from "utils/hooks/useMedia";
import useStartLive from "features/onboarding/guided/useStartLive";
import { OnboardingBackLink } from "features/onboarding/common/BackButton";
import ExplorePlatformButton from "features/onboarding/common/ExplorePlatformButton";
import { scrollToTop } from "utils/hooks/misc";
import usePracticeExchange from "features/onboarding/guided/usePracticeExchange";
import { useContext, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import styled, { css } from "styled-components";
import { Check, FlaskConical, Link2 } from "lucide-react";
import {
  BackArrow,
  NextArrow,
} from "features/onboarding/common/OnboardingIcons";
import {
  cardSurface,
  eyebrowText,
  IconTile,
  INLINE_ICON_PROPS,
  interactiveSurface,
  ONBOARDING_ICON_PROPS,
  reveal,
  revealFade,
  revealChildren,
  selectedSurface,
  SelectionCheck,
  softPanelBackground,
} from "features/onboarding/common/onboardingUi";
import { newLookType } from "common/NewLook/tokens";
import ArtStage from "features/onboarding/art/ArtStage";
import MethodThumb from "features/onboarding/art/MethodThumb";
import { DemoThumb } from "features/onboarding/art/MethodThumbs";
import Button from "common/Buttons/Button";
import LinkButton from "common/Buttons/LinkButton";
import Text from "common/Typography/Text";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces, WorkspaceType } from "@shared/types";
import useSharedWorkspace from "@shared/features/workspaces/useSharedWorkspace";
import { OnboardingContext } from "@shared/features/onboarding/Onboarding.context";
import { OnboardingDetailedStep } from "@shared/features/onboarding/types";
import { OnboardingStep as AnalyticsOnboardingStep } from "analytics/events";
import {
  ListStepLayout,
  HeadingBlock,
  HeroTitle,
  Lead,
  Eyebrow,
  SectionTitle,
  SmallNote,
} from "features/onboarding/guided/GuidedWelcome";
import {
  TradingIntent,
  useTradingIntent,
} from "features/onboarding/guided/intent";
import {
  ONBOARDING_GUIDED_DEMO_ID,
  ONBOARDING_LIVE_PATH_ID,
  ONBOARDING_INTENT_ID,
  ONBOARDING_METHOD_NEXT_ID,
  ONBOARDING_MODE_BACK_ID,
  ONBOARDING_MODE_CHOICE_ID,
  ONBOARDING_CHANGE_PRACTICE_ID,
  ONBOARDING_SELECTED_PRACTICE_ID,
} from "testIds";

const IntentWelcome = () => {
  const track = useOnboardingAnalytics();
  const { t } = useTypedTranslation(LanguageNameSpaces.ONBOARDING);
  const isPhone = useMedia(["(max-width: 560px)"], [true], false);
  const location = useLocation();
  const history = useHistory();
  const query = new URLSearchParams(location.search);
  const { intent, availableIntents } = useTradingIntent();
  const { exchange: practiceExchange, exchangeDisplay } = usePracticeExchange();
  const { changeStep } = useContext(OnboardingContext)!;
  const { setWorkspaceType } = useSharedWorkspace({});
  const choosingMode = !!intent && query.get("startMode") === "1";
  useOnboardingStepView(choosingMode ? "trading_mode" : "trading_method");
  const titleRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    titleRef.current?.focus({ preventScroll: true });
    scrollToTop();
  }, [choosingMode]);
  const choose = (method: TradingIntent): void => {
    track("Trading Method Selected", { trading_method: method });
    if (method !== intent) {
      query.delete("groupID");
      query.delete("botUUID");
      query.delete("exchange");
      query.delete("account-id");
      query.delete("setup");
      query.delete("preset");
    }
    query.set("method", method);
    history.replace({ ...location, search: query.toString() });
  };
  const showModes = (show: boolean): void => {
    if (show) query.set("startMode", "1");
    else query.delete("startMode");
    history.replace({ ...location, search: query.toString() });
  };
  const startDemo = (
    method: TradingIntent = intent || "dca",
    autoPrepare = true
  ): void => {
    track("Trading Mode Selected", {
      trading_method: method,
      trading_mode: "demo",
    });
    setWorkspaceType(WorkspaceType.DEMO);
    changeStep(
      autoPrepare
        ? method === "dca"
          ? OnboardingDetailedStep.COPY_DCA_BOT
          : OnboardingDetailedStep.TRADING_METHOD
        : OnboardingDetailedStep.CONNECT_EXCHANGE,
      {
        ...(method !== intent ? { groupID: "", botUUID: "" } : {}),
        guided: "1",
        prepareDemo: "",
        setup: "",
        preset: "",
        browse: "",
        "account-id": "",
        changePractice: "",
        method,
        exchange: practiceExchange,
      }
    );
  };
  const startLive = useStartLive();
  const hasLiveAccount = !!useSelectRealUser()?.has_valid_clients_connected;
  const [hovered, setHovered] = useState<TradingIntent>();

  return (
    <ListStepLayout>
      {choosingMode && (
        <OnboardingBackLink
          type="button"
          onClick={() => {
            track("Back Clicked", { from_step: "trading_mode" });
            showModes(false);
          }}
          data-testid={ONBOARDING_MODE_BACK_ID}
          aria-label={t("intent.change_method")}
        >
          <BackArrow /> <BackLabel>{t("intent.change_method")}</BackLabel>
          {/* Phones: the step line is the back row's text. */}
          <BackStep>{t("intent.mode_step")}</BackStep>
        </OnboardingBackLink>
      )}
      <IntentHeading $spaced={!choosingMode}>
        <Eyebrow data-on-back-row={choosingMode || undefined}>
          {t(choosingMode ? "intent.mode_step" : "intent.method_step")}
        </Eyebrow>
        <PageTitle ref={titleRef} tabIndex={-1}>
          {choosingMode
            ? t(
                intent === "signals"
                  ? "intent.signals_mode_title"
                  : "intent.mode_title",
                { method: t(`intent.${intent!}.name`) }
              )
            : t("intent.title")}
        </PageTitle>
        <MethodLead $choosingMode={choosingMode}>
          {t(
            choosingMode
              ? "intent.mode_description"
              : "intent.choose_description"
          )}
        </MethodLead>
      </IntentHeading>
      {choosingMode ? (
        <>
          <ModeGrid data-testid={ONBOARDING_MODE_CHOICE_ID}>
            <ModeCard>
              {intent && (
                <ModeStage height={190} phoneHeight={112}>
                  <MethodThumb method={intent} />
                </ModeStage>
              )}
              <MethodIcon>
                <IconTile>
                  <Link2 {...ONBOARDING_ICON_PROPS} />
                </IconTile>
              </MethodIcon>
              <SectionTitle>
                {t(
                  hasLiveAccount ? "intent.live_account" : "intent.live_title"
                )}
              </SectionTitle>
              <Text type="medium">
                {t(
                  hasLiveAccount
                    ? "intent.live_confirm"
                    : "intent.live_description"
                )}
              </Text>
              {!hasLiveAccount && (
                <ModeDetails>
                  <Text>
                    <Check {...INLINE_ICON_PROPS} />
                    {t("intent.live_account")}
                  </Text>
                  <Text>
                    <Check {...INLINE_ICON_PROPS} />
                    {t("intent.live_confirm")}
                  </Text>
                </ModeDetails>
              )}
              <Button
                type="primary"
                size="large"
                onClick={startLive}
                data-testid={ONBOARDING_LIVE_PATH_ID}
              >
                {t(
                  hasLiveAccount ? "intent.continue" : "guided.connect_exchange"
                )}{" "}
                <NextArrow />
              </Button>
            </ModeCard>
            <ModeCard>
              <ModeStage height={190} phoneHeight={112}>
                <DemoThumb />
              </ModeStage>
              <MethodIcon>
                <IconTile>
                  <FlaskConical {...ONBOARDING_ICON_PROPS} />
                </IconTile>
              </MethodIcon>
              <SectionTitle>{t("intent.demo_title")}</SectionTitle>
              <Text type="medium">{t("intent.demo_description")}</Text>
              <ModeDetails>
                <Text>
                  <Check {...INLINE_ICON_PROPS} />
                  {t("intent.demo_funds")}
                </Text>
                <Text>
                  <Check {...INLINE_ICON_PROPS} />
                  {t("intent.demo_no_connection")}
                </Text>
              </ModeDetails>
              <DemoExchangeRow data-testid={ONBOARDING_SELECTED_PRACTICE_ID}>
                <Text>{exchangeDisplay}</Text>
                <LinkButton
                  type="link"
                  data-testid={ONBOARDING_CHANGE_PRACTICE_ID}
                  onClick={() => startDemo(intent!, false)}
                >
                  {t(
                    isPhone
                      ? "walkthrough.change"
                      : "intent.change_demo_exchange"
                  )}
                </LinkButton>
              </DemoExchangeRow>
              <Button
                type="default"
                size="large"
                onClick={() => startDemo()}
                data-testid={ONBOARDING_GUIDED_DEMO_ID}
              >
                {t("intent.try_demo")} <NextArrow />
              </Button>
            </ModeCard>
          </ModeGrid>
          <SmallNote>{t("intent.exchange_freedom")}</SmallNote>
        </>
      ) : (
        <>
          <MethodGrid aria-label={t("intent.title")}>
            {availableIntents.map((method) => (
              <MethodChoice
                key={method}
                type="button"
                $selected={intent === method}
                aria-pressed={intent === method}
                data-testid={`${ONBOARDING_INTENT_ID}-${method}`}
                onClick={() => choose(method)}
                onMouseEnter={() => setHovered(method)}
                onMouseLeave={() => setHovered(undefined)}
              >
                <ThumbStage height={164} phoneHeight={84}>
                  <MethodThumb
                    method={method}
                    play={intent === method || hovered === method}
                  />
                  <StageCheck $selected={intent === method} aria-hidden="true">
                    {intent === method && <Check size={14} strokeWidth={2.5} />}
                  </StageCheck>
                </ThumbStage>
                <SectionTitle>{t(`intent.${method}.name`)}</SectionTitle>
                <Text
                  type="medium"
                  data-testid={`${ONBOARDING_INTENT_ID}-${method}-description`}
                >
                  {t(`intent.${method}.description`)}
                </Text>
              </MethodChoice>
            ))}
          </MethodGrid>
          <NextStep>
            {/* Phones: the selected method's line and Continue form the
                bottom bar; wider screens keep this panel's own layout. */}
            <PhoneActionBar phoneOnly={true}>
              <NextText aria-live="polite" data-next-text="">
                <Eyebrow>
                  {t(intent ? "intent.selected" : "intent.choose_prompt")}
                </Eyebrow>
                <SectionTitle>
                  {intent
                    ? t(`intent.${intent}.name`)
                    : t("intent.choose_hint")}
                </SectionTitle>
                {intent && (
                  <SelectedDescription
                    type="medium"
                    data-testid={`${ONBOARDING_INTENT_ID}-selected-description`}
                  >
                    {t(`intent.${intent}.description`)}
                  </SelectedDescription>
                )}
              </NextText>
              <Button
                type="primary"
                size="large"
                disabled={!intent}
                onClick={() => showModes(true)}
                data-testid={ONBOARDING_METHOD_NEXT_ID}
              >
                {/* Phones: the chosen card names the method; "Continue" fits. */}
                {intent && !isPhone
                  ? t(
                      intent === "signals"
                        ? "intent.continue_signals"
                        : "intent.continue_method",
                      {
                        method: t(`intent.${intent}.name`),
                      }
                    )
                  : t("intent.continue")}
                <NextArrow />
              </Button>
            </PhoneActionBar>
          </NextStep>
        </>
      )}
      <Explore>
        <ExplorePlatformButton
          onboardingStep={AnalyticsOnboardingStep.TRADING_METHOD}
        />
      </Explore>
    </ListStepLayout>
  );
};
export default IntentWelcome;

const BackLabel = styled.span`
  @media (max-width: 560px) {
    display: none;
  }
`;
const BackStep = styled.span`
  display: none;
  @media (max-width: 560px) {
    display: inline;
    ${eyebrowText};
  }
`;
const IntentHeading = styled(HeadingBlock)<{ $spaced: boolean }>`
  @media (max-width: 560px) {
    && > [data-on-back-row] {
      display: none;
    }
  }
  /* Phones: left-aligned like the walkthrough that follows, right under the
     back link's tap area. Its eyebrow is the step line here (no journey
     line on these steps), so it stays. */
  @media (max-width: 560px) {
    padding-top: 0;
    text-align: left;
    > div:first-child {
      display: flex;
    }
    > div {
      justify-content: flex-start;
    }
    h1 {
      font-size: 21px;
      line-height: 28px;
    }
    p {
      margin: 0;
      font-size: 14px;
      line-height: 20px;
    }
  }
  ${(p) =>
    p.$spaced &&
    css`
      padding-top: 24px;
      @media (max-width: 560px) {
        padding-top: 12px;
      }
    `}
`;
const PageTitle = styled(HeroTitle)`
  scroll-margin-top: 24px;
  &:focus {
    outline: none;
  }
`;
const MethodLead = styled(Lead)<{ $choosingMode: boolean }>`
  @media (max-width: 560px) {
    display: ${(p) => (p.$choosingMode ? "block" : "none")};
  }
`;
const MethodGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  @media (max-width: 560px) {
    gap: 10px;
  }
  ${revealChildren(3, 4)};
`;
const MethodChoice = styled.button<{ $selected: boolean }>`
  ${cardSurface};
  ${interactiveSurface};
  ${(p) => p.$selected && selectedSurface};
  text-align: left;
  font: inherit;
  color: inherit;
  padding: 8px 8px 16px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  h2 {
    ${newLookType.panelTitle};
    margin: 12px 8px 4px;
  }
  > p {
    ${newLookType.body};
    color: ${(p) => p.theme.colors.secondaryText};
    margin: 0 8px;
  }
  @media (max-width: 560px) {
    min-height: 0;
    padding: 4px 4px 12px;
    justify-content: flex-start;
    h2 {
      margin: 8px 8px 0;
    }
    > p {
      display: none;
    }
  }
`;
const ThumbStage = styled(ArtStage)`
  width: 100%;
  padding: 8px;
  @media (max-width: 560px) {
    padding: 4px;
  }
`;
const StageCheck = styled(SelectionCheck)`
  position: absolute;
  top: 10px;
  right: 10px;
  @media (max-width: 560px) {
    top: 6px;
    right: 6px;
    width: 20px;
    height: 20px;
  }
`;
const ModeStage = styled(ArtStage)`
  margin-bottom: 20px;
  @media (max-width: 560px) {
    margin-bottom: 14px;
  }
`;
const MethodIcon = styled.div`
  display: flex;
  font-size: 24px;
  color: ${(p) => p.theme.colors.primaryBlue};
`;
const SelectedDescription = styled(Text)`
  display: none;
  @media (max-width: 560px) {
    display: block;
    margin: 6px 0 0;
    font-size: 14px;
    line-height: 1.45;
  }
`;
const NextStep = styled.section`
  ${cardSurface};
  ${reveal(7)};
  /* Phones: it holds the fixed Continue bar, which a transform would pin to
     the panel (mid-screen) until the motion ends. */
  @media (max-width: 560px) {
    ${revealFade(7)};
  }
  position: sticky;
  bottom: 0;
  z-index: 2;
  margin-top: 16px;
  padding: 12px 12px 12px 20px;
  /* It floats over the cards while they scroll under it. */
  box-shadow: ${(p) =>
    p.theme.isDarkMode
      ? "0 8px 24px rgba(0, 0, 0, 0.32)"
      : "0 8px 24px rgba(13, 20, 78, 0.06)"};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  h2 {
    ${newLookType.panelTitle};
    margin: 4px 0 0;
  }
  button {
    white-space: normal;
    height: auto;
    min-height: 40px;
  }
  @media (max-width: 650px) {
    position: static;
    margin-top: 12px;
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
    padding: 12px;
    box-shadow: none;
    h2 {
      margin: 0;
    }
    [data-next-text] > div {
      display: none;
    }
  }
  /* Phones: no panel; its line and Continue are the bottom bar. */
  @media (max-width: 560px) {
    margin: 0;
    padding: 0;
    border: 0;
    background: transparent;
    [data-next-text] {
      text-align: left;
      h2 {
        display: none;
      }
      p,
      span {
        display: block;
        margin: 0 0 8px;
        font-size: 13px;
        line-height: 18px;
      }
    }
  }
`;
const NextText = styled.div``;
const Explore = styled.div`
  @media (max-width: 650px) {
    margin-bottom: 24px;
  }
  margin-top: 24px;
  text-align: center;
  button {
    white-space: normal;
    height: auto;
  }
  ${reveal(8)};
`;
const ModeGrid = styled(MethodGrid)`
  gap: 20px;
  margin: 8px 0 20px;
  @media (max-width: 560px) {
    gap: 12px;
    margin: 0 0 16px;
  }
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;
const ModeCard = styled.section`
  ${cardSurface};
  padding: 8px 8px 16px;
  display: flex;
  flex-direction: column;
  > * {
    margin-left: 12px;
    margin-right: 12px;
  }
  > ${ModeStage} {
    margin: 0 0 16px;
  }
  > ${MethodIcon} {
    display: none;
  }
  > p {
    min-height: 40px;
    ${newLookType.body};
    color: ${(p) => p.theme.colors.secondaryText};
  }
  h2 {
    ${newLookType.panelTitle};
    margin: 0 12px 4px;
  }
  button {
    margin-top: auto;
  }
  /* Phones: the illustration as a short banner over the option's title,
     line and button, so both options fit without losing the motion. */
  @media (max-width: 560px) {
    padding: 6px 6px 16px;
    > * {
      margin-left: 10px;
      margin-right: 10px;
    }
    > ${ModeStage} {
      display: flex;
      margin: 0 0 12px;
    }
    h2 {
      margin: 0 10px 2px;
    }
    > p {
      min-height: 0;
      margin: 0 10px 14px;
    }
    button {
      margin-top: 0;
    }
  }
`;
const ModeDetails = styled.div`
  display: grid;
  gap: 8px;
  margin: 16px 12px 20px;
  > * {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0;
    ${newLookType.body};
  }
  svg {
    flex-shrink: 0;
    color: ${(p) => p.theme.colors.secondaryText};
  }
  @media (max-width: 560px) {
    display: none;
  }
`;
const DemoExchangeRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 4px 12px;
  margin-bottom: 16px;
  padding: 4px 4px 4px 12px;
  border-radius: ${(p) => p.theme.radii.control}px;
  background: ${(p) => softPanelBackground(p.theme)};
  ${newLookType.body};
  button {
    padding: 0 8px;
    margin-top: 0;
  }
  /* Phones: one line, the exchange and a short "Change". */
  @media (max-width: 560px) {
    flex-wrap: nowrap;
    > :first-child {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    button {
      flex: none;
    }
  }
`;
